import { useMutation } from "@tanstack/react-query"
import { Dialog } from "radix-ui"
import { FaTimes } from "@react-icons/all-files/fa/FaTimes"
import { Value } from "@stringtale/react"
import Field from "app/core/components/Field"
import useToast from "app/toasts/hooks/useToast"
import { Dispatch, SetStateAction, Suspense, useState } from "react"
import { LIST_COLORS } from "theme/colors"
import {
  Title,
  Buttons,
  CancelButton,
  Close,
  Content,
  Overlay as OverlayBase,
  Root as RootBase,
  SubmitButton,
  ScrollArea,
} from "ui/Modal"
import Stack from "ui/Stack"
import { MODAL_Z_INDEX } from "theme/consts"
import createFormFactory from "utils/form/createForm"
import { useQueryClient } from "@tanstack/react-query"
import { trpc } from "app/trpc"
import type { AddUserPlaylistResult } from "../mutations/addUserPlaylist"
import { AddUserListInput } from "../validations"
import Color, { ColorGroup } from "./Color"
import FieldArea from "app/core/components/FieldArea"
import PlaylistAccessLevels from "./PlaylistAccessLevels"
import { z } from "zod"
import SelectGroupModal from "app/groups/components/SelectGroupModal"
import { GroupSelectItem } from "app/groups/components/GroupSelectItem"
import Hr from "ui/Hr"
import Text from "ui/Text"
import { PX14 } from "theme/sizes"
import type { Playlist } from "db/client"
import {
  GradeGroup,
  Root as GradesCheckbox,
} from "app/schoolbeheer/components/groups/Grades"
import styled from "@emotion/styled"
import toggleArray from "utils/global/toggleArray"
import { useGradesFromContext } from "app/core/providers/SystemInfoProvider"
import Center from "ui/Center"
import Loader from "ui/Loader"

export type SelectPlaylistAccessLevels = z.infer<
  typeof AddUserListInput
>["accessLevel"]

const Overlay = styled(OverlayBase)`
  z-index: ${MODAL_Z_INDEX + 3};
`

const Root = styled(RootBase)`
  z-index: ${MODAL_Z_INDEX + 4};
`

const FormLabel = styled.label`
  font-weight: 600;
`

const form = createFormFactory({
  validationSchema: AddUserListInput,
})

const NewPlaylistModal = ({
  open,
  onOpenChange,

  onClose,
}: {
  open: boolean
  onOpenChange: Dispatch<SetStateAction<boolean>>
  onClose: (playlist?: AddUserPlaylistResult["userLists"][number]) => void
}) => {
  const grades = useGradesFromContext()
  const queryClient = useQueryClient()
  const addPlaylistMutation = useMutation(
    trpc.playlists.addUserPlaylist.mutationOptions()
  )
  const { isPending } = addPlaylistMutation
  const addSchoolPlaylistMutation = useMutation(
    trpc.schoolbeheer.addSchoolPlaylist.mutationOptions()
  )
  const [serverErrors, setServerErrors] = useState()
  const [color, setColor] = useState(LIST_COLORS[0])
  const addToast = useToast()
  const [accessLevel, setAccessLevel] =
    useState<SelectPlaylistAccessLevels>("CURRENT_GROUP")
  const [data, setData] = useState<z.infer<typeof AddUserListInput> | null>(
    null
  )
  const [activeGrades, setActiveGrades] = useState<number[]>([])

  const onSubmit = async (
    data: z.infer<typeof AddUserListInput>,
    selectedGroups?: number[]
  ) => {
    if (data.accessLevel === "SCHOOL") {
      const playlist = await addSchoolPlaylistMutation.mutateAsync({
        displayTitle: data.displayTitle as string,
        description: data.description as string,
        color,
        grades: activeGrades,
      })
      addToast({
        content: (
          <Value name="apps.web.src.themes.components.newplaylistmodal.schoollijst_is_aangemaakt">
            {`Schoollijst is aangemaakt.`}
          </Value>
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.playlists.getGroupedUserPlaylists.queryKey(),
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.schoolbeheer.getSchoolLists.queryKey(),
      })
      onClose(playlist)
    } else {
      const playlist = await addPlaylistMutation.mutateAsync({
        accessLevel:
          data.accessLevel === "PERSONAL" ? "PERSONAL" : "CURRENT_GROUP",
        displayTitle: data.displayTitle as string,
        description: data.description as string,
        color,
        selectedGroups,
      })
      addToast({
        content: (
          <Value name="apps.web.src.themes.components.newplaylistmodal.lijst_is_aangemaakt">
            {`Lijst is aangemaakt.`}
          </Value>
        ),
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.playlists.getGroupedUserPlaylists.queryKey(),
      })
      onClose(playlist.userLists[0])
    }
  }

  // if (data) {
  //   return (

  //   )
  // }
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Suspense>
        <Dialog.Overlay asChild>
          <Overlay />
        </Dialog.Overlay>
        <Dialog.Content>
          {/* Regular view to create a playlist, see below for multi group select */}
          {!data && (
            <Root>
              <ScrollArea>
                <form.Form
                  noValidate
                  defaultValues={{
                    color,
                    accessLevel,
                  }}
                  onSubmit={async (data) => {
                    if (accessLevel === "SELECTED_GROUPS") {
                      setData(data)
                      return
                    }
                    try {
                      onSubmit(data)
                    } catch (e) {
                      setServerErrors(e)
                    }
                  }}
                >
                  <Content>
                    <Suspense
                      fallback={
                        <Center>
                          <Loader />
                        </Center>
                      }
                    >
                      <Stack gap="24px">
                        <Stack gap="16px">
                          <Dialog.Title asChild>
                            <Title>
                              <Value name="apps.web.src.playlists.components.newplaylistmodal.nieuwe_afspeellijst">
                                Nieuwe afspeellijst
                              </Value>
                            </Title>
                          </Dialog.Title>
                          <Hr />
                        </Stack>

                        <PlaylistAccessLevels
                          form={form}
                          onValueChange={(v) =>
                            setAccessLevel(v as SelectPlaylistAccessLevels)
                          }
                          accessLevel={accessLevel}
                        />
                        {accessLevel === "SCHOOL" && (
                          <Stack gap={"6px"}>
                            <FormLabel>
                              <Value
                                name="apps.web.src.schoolbeheer.components.schoollists.newschoollistmodal.leerjaar_van_de_schoollijst"
                                version="1"
                              >
                                Leerjaren van de schoollijst
                              </Value>
                            </FormLabel>
                            <GradeGroup>
                              {grades.map((grade) => (
                                <GradesCheckbox
                                  name="grades[]"
                                  key={grade.id}
                                  value={`${grade.id}`}
                                  checked={activeGrades.includes(grade.id)}
                                  onCheckedChange={() =>
                                    setActiveGrades(toggleArray(grade.id))
                                  }
                                >
                                  {grade.number}
                                </GradesCheckbox>
                              ))}
                            </GradeGroup>
                          </Stack>
                        )}
                        <Field
                          name="displayTitle"
                          label={
                            <Value name="apps.web.src.playlists.components.newplaylistmodal.naam">
                              Naam
                            </Value>
                          }
                          form={form}
                        />
                        <FieldArea
                          name="description"
                          label={
                            <Value name="apps.web.src.playlists.components.newplaylistmodal.omschrijving">
                              Omschrijving
                            </Value>
                          }
                          form={form}
                        />
                        <ColorGroup onValueChange={setColor} value={color}>
                          {LIST_COLORS.map((color) => (
                            <Color key={color.toString()} value={color} />
                          ))}
                        </ColorGroup>
                        <Buttons>
                          <Dialog.Close asChild>
                            <CancelButton type="button">
                              <Value name="apps.web.src.playlists.components.newplaylistmodal.annuleren">
                                Annuleren
                              </Value>
                            </CancelButton>
                          </Dialog.Close>
                          <SubmitButton type="submit" disabled={isPending}>
                            {accessLevel === "SELECTED_GROUPS" ? (
                              <Value name="apps.web.src.playlists.components.newplaylistmodal.volgende">
                                Volgende
                              </Value>
                            ) : (
                              <Value name="apps.web.src.playlists.components.newplaylistmodal.opslaan">
                                Opslaan
                              </Value>
                            )}
                          </SubmitButton>
                        </Buttons>
                      </Stack>
                    </Suspense>
                  </Content>
                </form.Form>
              </ScrollArea>
              <Dialog.Close asChild>
                <Close>
                  <FaTimes />
                </Close>
              </Dialog.Close>
            </Root>
          )}
          {/* Multi-group select view  */}
          <SelectGroupModal
            open={!!data}
            onOpenChange={(open) => {
              if (!open) {
                setData(null)
                onOpenChange(false)
                onClose()
                setAccessLevel("CURRENT_GROUP")
              }
            }}
            onSubmit={async (selectedGroups) => {
              if (!data) return
              await onSubmit(data, selectedGroups)
            }}
            onClose={() => {
              setData(null)
              onClose()
              setAccessLevel("CURRENT_GROUP")
            }}
            onRenderItem={(props) => <GroupSelectItem {...props} />}
          >
            <Stack gap="16px">
              <Title>
                <Value name="apps.web.src.playlists.components.newplaylistmodal.zichtbaar_voor_andere_groepen">
                  Zichtbaar voor andere groepen
                </Value>
              </Title>
              <Hr />
            </Stack>
            <Text size={PX14}>
              <Value name="apps.web.src.playlists.components.newplaylistmodal.selecteer_n_of_meerdere_groepen_om_waarvoor_je_deze_lijst_wilt_opslaan_duo_s_uit_geselecteerde_groepen_krijgen_deze_lijst_automatisch_te_zien">
                {`Selecteer één of meerdere groepen waarvoor je deze lijst wilt
            opslaan. Duo's uit geselecteerde groepen krijgen deze lijst
            automatisch te zien.`}
              </Value>
            </Text>
          </SelectGroupModal>
        </Dialog.Content>
      </Suspense>
    </Dialog.Root>
  )
}

export default NewPlaylistModal
